// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import {
  CognitoIdentityProviderClient,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  ConfirmForgotPasswordCommand,
  GetUserCommand,
} from "@aws-sdk/client-cognito-identity-provider";

interface Config {
  region: string;
  userPoolId: string;
  clientId: string;
}

let config: Config | undefined;
let cognitoClient: CognitoIdentityProviderClient;

const configPromise = (async () => {
  const response = await fetch('/config.json');
  if (!response.ok) {
    throw new Error('Failed to load config');
  }
  config = await response.json() as Config;
  
  cognitoClient = new CognitoIdentityProviderClient({
    region: config.region,
  });
})();

export const getCognitoClient = async () => {
  await configPromise;
  return cognitoClient;
};

export const signIn = async (username: string, password: string) => {
  const client = await getCognitoClient();
  const params = {
    AuthFlow: "USER_PASSWORD_AUTH" as const,
    ClientId: config?.clientId,
    AuthParameters: {
      USERNAME: username,
      PASSWORD: password,
    },
  };

  try {
    const command = new InitiateAuthCommand(params);
    const { ChallengeName, AuthenticationResult, Session } = await client.send(command);

    if (ChallengeName === "NEW_PASSWORD_REQUIRED") {
      sessionStorage.setItem("cognitoSession", Session || "");
      sessionStorage.setItem("cognitoUsername", username || "");
      return "NEW_PASSWORD_REQUIRED";
    }

    if (AuthenticationResult) {
      sessionStorage.setItem("idToken", AuthenticationResult.IdToken || "");
      sessionStorage.setItem("accessToken", AuthenticationResult.AccessToken || "");
      sessionStorage.setItem("refreshToken", AuthenticationResult.RefreshToken || "");
      window.dispatchEvent(new Event('authStateChanged'));
      return AuthenticationResult;
    }
  } catch (error) {
    console.error("Error signing in: ", error);
    throw error;
  }
};

export const signOut = async () => {
  sessionStorage.clear();
  window.dispatchEvent(new Event('authStateChanged'));
};

export const setNewPassword = async (newPassword: string) => {
  const session = sessionStorage.getItem("cognitoSession");
  const username = sessionStorage.getItem("cognitoUsername");
  if (!session) {
    throw new Error("Session not found. Please sign in again.");
  }

  const params = {
    ChallengeName: "NEW_PASSWORD_REQUIRED" as const,
    ClientId: config?.clientId,
    Session: session,
    ChallengeResponses: {
      USERNAME: username ?? '',
      NEW_PASSWORD: newPassword,
    },
  };

  try {
    const command = new RespondToAuthChallengeCommand(params);
    const response = await cognitoClient.send(command);

    if (response.AuthenticationResult) {
      sessionStorage.setItem("idToken", response.AuthenticationResult.IdToken || "");
      sessionStorage.setItem("accessToken", response.AuthenticationResult.AccessToken || "");
      sessionStorage.setItem("refreshToken", response.AuthenticationResult.RefreshToken || "");
      window.dispatchEvent(new Event('authStateChanged'));
      sessionStorage.removeItem("cognitoUsername");
      sessionStorage.removeItem("cognitoSession");
      return response.AuthenticationResult;
    }
  } catch (error) {
    console.error("Error setting new password: ", error);
    throw error;
  }
};

export const resetPassword = async (email: string) => {
  try {
    const params = {
      ClientId: config?.clientId,
      Username: email,
    };
    sessionStorage.setItem("restorePasswordEmail", email || "");
    const command = new ForgotPasswordCommand(params);
    await cognitoClient.send(command);
  } catch (error) {
    throw error;
  }
};

export const confirmNewPassword = async (newPassword: string) => {
  try {
    const params = {
      ClientId: config?.clientId,
      Username: sessionStorage.getItem("restorePasswordEmail") || "",
      ConfirmationCode: sessionStorage.getItem("restorePasswordCode") || "",
      Password: newPassword,
    };

    const command = new ConfirmForgotPasswordCommand(params);
    await cognitoClient.send(command);
  } catch (error) {
    if (error instanceof Error && error.name === "CodeMismatchException") {
      throw new Error("Invalid confirmation code");
    } else if (error instanceof Error) {
      throw error;
    } else {
      throw new Error("Unknown error");
    }
  }
};

interface UserData {
  fullName: string;
  email: string;
}

export const getUserData = async (): Promise<UserData> => {
  try {
    const accessToken = sessionStorage.getItem("accessToken");
    if (!accessToken) {
      throw new Error("User is not authorized");
    }

    const client = await getCognitoClient();
    const command = new GetUserCommand({ AccessToken: accessToken });
    const response = await client.send(command);

    const attributes = response.UserAttributes?.reduce((acc, attr) => {
      if (attr.Name && attr.Value) {
        acc[attr.Name] = attr.Value;
      }
      return acc;
    }, {} as Record<string, string>);

    if (!attributes) {
      throw new Error("Failed to get user attributes");
    }

    return {
      fullName: `${attributes.given_name} ${attributes.family_name}`,
      email: attributes.email
    };
  } catch (error: any) {
    console.error("Error getting user data: ", error);
    if(error.__type === "NotAuthorizedException") {
      signOut();
    }
    throw error;
  }
};


